import React, { useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { useStore } from "../../../helpers/helpers";
import { htmlValidation } from "../../../helpers/utils";
import { AgGridReact } from "@ag-grid-community/react";
import { ModuleRegistry } from "@ag-grid-community/core";
import { ClientSideRowModelModule } from "@ag-grid-community/client-side-row-model";

import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";

import {
    FaWhatsapp,
    FaPhoneAlt,
    FaTrashAlt,
    FaBullhorn,
    FaClipboardList,
    FaCheckCircle,
    FaClock,
    FaTimesCircle,
    FaEllipsisH,
} from "react-icons/fa";

ModuleRegistry.registerModules([ClientSideRowModelModule]);

type CampaignFilters = {
    channel: string;
    target: string;
    status: string;
};

const initialFilters: CampaignFilters = {
    channel: "",
    target: "",
    status: "",
};

const uniqueValues = (values: unknown[]) =>
    Array.from(new Set(values.map((value) => String(value ?? "").trim()).filter(Boolean))).sort((a, b) =>
        a.localeCompare(b)
    );

const normalizeStatus = (status: unknown) =>
    String(status ?? "")
        .trim()
        .toLowerCase();

const CampaignFilterHeader = (params: any) => (
    <div className="flex h-full items-center">
        <span className="truncate text-[11px] font-bold text-slate-600" title={params.displayName}>
            {params.displayName}
        </span>
    </div>
);

const VoterReachOutListCampaign = observer((props: any) => {
    const { voterReachOutStore } = useStore();
    const { reachOutCampaignList } = voterReachOutStore;

    const { setPopOver, deleteMessage } = props;

    const [filters, setFilters] = useState<CampaignFilters>(initialFilters);

    const [search, setSearch] = useState("");
    const [dateRange, setDateRange] = useState("");

    const campaigns = useMemo(() => reachOutCampaignList || [], [reachOutCampaignList]);

    // Summary counts use the complete campaign list,
    // not the filtered table rows.
    const summary = useMemo(() => {
        const review = campaigns.filter(
            (item: any) => normalizeStatus(item?.status) === "campaign-manager-review"
        ).length;

        const finished = campaigns.filter((item: any) => normalizeStatus(item?.status) === "finished").length;

        const failed = campaigns.filter((item: any) => normalizeStatus(item?.status) === "failed").length;

        const others = Math.max(0, campaigns.length - review - finished - failed);

        return {
            total: campaigns.length,
            review,
            finished,
            failed,
            others,
        };
    }, [campaigns]);

    const summaryCards = [
        {
            label: "Total Campaigns",
            value: summary.total,
            description: "All campaigns",
            icon: FaClipboardList,
            iconStyle: "bg-indigo-50 text-indigo-600",
            valueStyle: "text-indigo-600",
            borderStyle: "border-indigo-100",
            decorationStyle: "bg-indigo-50",
        },
        {
            label: "Under Review",
            value: summary.review,
            description: "Awaiting review",
            icon: FaClock,
            iconStyle: "bg-blue-50 text-blue-600",
            valueStyle: "text-blue-600",
            borderStyle: "border-blue-100",
            decorationStyle: "bg-blue-50",
        },
        {
            label: "Finished",
            value: summary.finished,
            description: "Completed campaigns",
            icon: FaCheckCircle,
            iconStyle: "bg-emerald-50 text-emerald-600",
            valueStyle: "text-emerald-600",
            borderStyle: "border-emerald-100",
            decorationStyle: "bg-emerald-50",
        },
        {
            label: "Failed",
            value: summary.failed,
            description: "Failed campaigns",
            icon: FaTimesCircle,
            iconStyle: "bg-rose-50 text-rose-600",
            valueStyle: "text-rose-600",
            borderStyle: "border-rose-100",
            decorationStyle: "bg-rose-50",
        },
        {
            label: "Others",
            value: summary.others,
            description: "Queued and other statuses",
            icon: FaEllipsisH,
            iconStyle: "bg-amber-50 text-amber-600",
            valueStyle: "text-amber-600",
            borderStyle: "border-amber-100",
            decorationStyle: "bg-amber-50",
        },
    ];

    const filterOptions = useMemo(
        () => ({
            channel: uniqueValues(
                campaigns.map((item: any) => {
                    if (item?.msg?.channel === "wa") return "WhatsApp";
                    if (item?.msg?.channel === "voicecall") return "Voice Call";
                    return item?.msg?.channel;
                })
            ),
            target: uniqueValues(campaigns.map((item: any) => item?.tgt?.name)),
            status: uniqueValues(campaigns.map((item: any) => item?.status)),
        }),
        [campaigns]
    );

    const filteredCampaigns = useMemo(() => {
        const query = search.trim().toLowerCase();
        const now = new Date();

        return campaigns.filter((item: any) => {
            const channel =
                item?.msg?.channel === "wa"
                    ? "WhatsApp"
                    : item?.msg?.channel === "voicecall"
                      ? "Voice Call"
                      : String(item?.msg?.channel ?? "");

            const searchableText = [item?.name, item?.msg?.ds?.name, item?.tgt?.name]
                .map((value) => String(value ?? ""))
                .join(" ")
                .toLowerCase();

            const updatedAt = item?.updated_at || item?.updated || item?.created_at || item?.created;

            const updatedDate = updatedAt ? new Date(updatedAt) : null;

            let dateMatch = true;

            if (dateRange && (!updatedDate || Number.isNaN(updatedDate.getTime()))) {
                dateMatch = false;
            } else if (dateRange === "today" && updatedDate) {
                dateMatch = updatedDate.toDateString() === now.toDateString();
            } else if (dateRange === "7days" && updatedDate) {
                dateMatch = now.getTime() - updatedDate.getTime() <= 7 * 24 * 60 * 60 * 1000;
            } else if (dateRange === "30days" && updatedDate) {
                dateMatch = now.getTime() - updatedDate.getTime() <= 30 * 24 * 60 * 60 * 1000;
            }

            return (
                (!query || searchableText.includes(query)) &&
                (!filters.channel || channel === filters.channel) &&
                (!filters.target || item?.tgt?.name === filters.target) &&
                (!filters.status || item?.status === filters.status) &&
                dateMatch
            );
        });
    }, [campaigns, filters, search, dateRange]);

    const handleFilterChange = (key: keyof CampaignFilters, value: string) => {
        setFilters((previous) => ({
            ...previous,
            [key]: value,
        }));
    };

    const resetFilters = () => {
        setFilters(initialFilters);
        setSearch("");
        setDateRange("");
    };

    const hasActiveFilters = Object.values(filters).some(Boolean) || Boolean(search) || Boolean(dateRange);

    const detailsReachOutCampaign = async (id: string, mode: string) => {
        const response = await voterReachOutStore.details_reachout_Campaign({ id });

        if (response?.status === 200 && mode === "edit") {
            voterReachOutStore.set_isEditOverlay(true);
        }
    };

    const getStatusDisplay = (status: string) => {
        const normalized = normalizeStatus(status);

        if (normalized === "finished") {
            return {
                label: "Finished",
                classes: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
                dot: "bg-emerald-500",
            };
        }

        if (normalized === "queued") {
            return {
                label: "Queued",
                classes: "bg-amber-50 text-amber-700 ring-amber-600/20",
                dot: "bg-amber-500",
            };
        }

        if (normalized === "campaign-manager-review") {
            return {
                label: "Under Review",
                classes: "bg-blue-50 text-blue-700 ring-blue-600/20",
                dot: "bg-blue-500",
            };
        }

        if (normalized === "failed") {
            return {
                label: "Failed",
                classes: "bg-rose-50 text-rose-700 ring-rose-600/20",
                dot: "bg-rose-500",
            };
        }

        return {
            label: status || "Unknown",
            classes: "bg-slate-100 text-slate-600 ring-slate-500/20",
            dot: "bg-slate-400",
        };
    };

    const columnDefs = useMemo(
        () => [
            {
                headerName: "Campaign",
                field: "name",
                minWidth: 210,
                flex: 1.2,
                sortable: true,
                suppressMenu: true,
                headerComponent: CampaignFilterHeader,
                cellRenderer: (params: any) => (
                    <button
                        type="button"
                        className="flex h-full max-w-full items-center gap-2 text-left font-semibold text-indigo-600 transition hover:text-indigo-800 hover:underline"
                        title={params.value || "View campaign"}
                        onClick={(event) => {
                            htmlValidation(event);
                            setPopOver?.(false);

                            voterReachOutStore.set_selected_overview_item(params.data);
                            voterReachOutStore.set_IsMessageOverviewOpen(true);

                            void detailsReachOutCampaign(params.data.id, "view");
                        }}
                    >
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                            <FaBullhorn />
                        </span>
                        <span className="truncate">{params.value || "Untitled campaign"}</span>
                    </button>
                ),
            },
            {
                headerName: "Channel",
                field: "msg.channel",
                minWidth: 150,
                flex: 0.8,
                sortable: true,
                suppressMenu: true,
                headerComponent: CampaignFilterHeader,
                valueGetter: (params: any) => {
                    const channel = params.data?.msg?.channel;
                    if (channel === "wa") return "WhatsApp";
                    if (channel === "voicecall") return "Voice Call";
                    return channel || "—";
                },
                cellRenderer: (params: any) => {
                    const isWhatsApp = params.value === "WhatsApp";
                    const isVoice = params.value === "Voice Call";

                    return (
                        <div className="flex h-full items-center">
                            <span
                                className={`inline-flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs font-semibold ${
                                    isWhatsApp
                                        ? "bg-emerald-50 text-emerald-700"
                                        : isVoice
                                          ? "bg-blue-50 text-blue-700"
                                          : "bg-slate-100 text-slate-600"
                                }`}
                            >
                                {isWhatsApp ? (
                                    <FaWhatsapp className="text-base" />
                                ) : isVoice ? (
                                    <FaPhoneAlt />
                                ) : null}
                                {params.value || "—"}
                            </span>
                        </div>
                    );
                },
            },
            {
                headerName: "Message",
                field: "msg.ds.name",
                minWidth: 170,
                flex: 1,
                sortable: true,
                suppressMenu: true,
                headerComponent: CampaignFilterHeader,
                valueGetter: (params: any) => params.data?.msg?.ds?.name || "—",
                cellStyle: { color: "#475569" },
            },
            {
                headerName: "Target Group",
                field: "tgt.name",
                minWidth: 170,
                flex: 1,
                sortable: true,
                suppressMenu: true,
                headerComponent: CampaignFilterHeader,
                valueGetter: (params: any) => params.data?.tgt?.name || "—",
                cellStyle: { color: "#475569" },
            },
            {
                headerName: "Success",
                field: "success_count",
                minWidth: 100,
                width: 110,
                sortable: true,
                suppressMenu: true,
                headerComponent: CampaignFilterHeader,
                valueGetter: (params: any) => params.data?.success_count ?? 0,
                cellStyle: {
                    color: "#047857",
                    fontWeight: 700,
                    textAlign: "center",
                },
            },
            {
                headerName: "Failed",
                field: "failure_count",
                minWidth: 95,
                width: 105,
                sortable: true,
                suppressMenu: true,
                headerComponent: CampaignFilterHeader,
                valueGetter: (params: any) => params.data?.failure_count ?? 0,
                cellStyle: {
                    color: "#E11D48",
                    fontWeight: 700,
                    textAlign: "center",
                },
            },
            {
                headerName: "Total",
                field: "tgt.count",
                minWidth: 90,
                width: 100,
                sortable: true,
                suppressMenu: true,
                headerComponent: CampaignFilterHeader,
                valueGetter: (params: any) => params.data?.tgt?.count ?? 0,
                cellStyle: {
                    color: "#334155",
                    fontWeight: 700,
                    textAlign: "center",
                },
            },
            {
                headerName: "Credits Spent",
                field: "more.credits_spent",
                minWidth: 125,
                width: 140,
                sortable: true,
                suppressMenu: true,
                headerComponent: CampaignFilterHeader,
                valueGetter: (params: any) => params.data?.more?.credits_spent ?? 0,
                valueFormatter: (params: any) => Number(params.value ?? 0).toLocaleString(),
                cellStyle: {
                    color: "#4F46E5",
                    fontWeight: 700,
                    textAlign: "center",
                },
            },
            {
                headerName: "Status",
                field: "status",
                minWidth: 145,
                width: 160,
                sortable: true,
                suppressMenu: true,
                headerComponent: CampaignFilterHeader,
                cellRenderer: (params: any) => {
                    const status = getStatusDisplay(params.value);

                    return (
                        <div className="flex h-full items-center">
                            <span
                                className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold ring-1 ring-inset ${status.classes}`}
                            >
                                <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`} />
                                {status.label}
                            </span>
                        </div>
                    );
                },
            },
            {
                headerName: "Actions",
                colId: "actions",
                minWidth: 90,
                width: 100,
                sortable: false,
                suppressMenu: true,
                headerComponent: CampaignFilterHeader,
                cellRenderer: (params: any) => (
                    <div className="flex h-full items-center justify-center">
                        <button
                            type="button"
                            title="Delete campaign"
                            aria-label={`Delete ${params.data?.name || "campaign"}`}
                            onClick={(event) => {
                                htmlValidation(event);
                                deleteMessage?.(params.data.id);
                            }}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition hover:bg-rose-50 hover:text-rose-600 focus:outline-none focus:ring-2 focus:ring-rose-100"
                        >
                            <FaTrashAlt />
                        </button>
                    </div>
                ),
            },
        ],
        [voterReachOutStore, setPopOver, deleteMessage]
    );

    const defaultColDef = useMemo(
        () => ({
            resizable: true,
            sortable: true,
            filter: false,
            suppressMenu: true,
            wrapHeaderText: true,
        }),
        []
    );

    return (
        <div className="space-y-4">
            {/* Campaign Summary Cards */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
                {summaryCards.map((card) => {
                    const Icon = card.icon;

                    return (
                        <div
                            key={card.label}
                            className={`group relative overflow-hidden rounded-2xl border ${card.borderStyle} bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-md`}
                        >
                            <div
                                className={`absolute right-0 top-0 h-24 w-24 -translate-y-7 translate-x-7 rounded-full ${card.decorationStyle} transition-transform duration-200 group-hover:scale-125`}
                            />

                            <div className="relative flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <p className="text-sm font-medium text-slate-500">{card.label}</p>

                                    <p
                                        className={`mt-3 text-3xl font-bold tracking-tight ${card.valueStyle}`}
                                    >
                                        {card.value.toLocaleString()}
                                    </p>

                                    <p className="mt-1 text-xs text-slate-400">{card.description}</p>
                                </div>

                                <div
                                    className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${card.iconStyle}`}
                                >
                                    <Icon className="text-lg" />
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Search and Filters */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6">
                <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h2 className="text-base font-bold text-slate-900">Filter Campaigns</h2>
                        <p className="mt-1 text-sm text-slate-500">
                            Narrow down campaigns by name, status, channel, target group, or date.
                        </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            type="button"
                            onClick={resetFilters}
                            disabled={!hasActiveFilters}
                            className="self-start rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 transition hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700 disabled:cursor-not-allowed disabled:opacity-40 sm:self-auto"
                        >
                            Reset filters
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
                    <div>
                        <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Search
                        </label>
                        <input
                            type="text"
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            placeholder="Search campaigns..."
                            aria-label="Search campaigns"
                            className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:bg-white focus:ring-4 focus:ring-indigo-50"
                        />
                    </div>

                    <div>
                        <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Status
                        </label>
                        <select
                            value={filters.status}
                            onChange={(event) => handleFilterChange("status", event.target.value)}
                            aria-label="Filter by campaign status"
                            className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 text-sm text-slate-700 outline-none transition focus:border-indigo-400 focus:bg-white focus:ring-4 focus:ring-indigo-50"
                        >
                            <option value="">All Statuses</option>
                            {filterOptions.status.map((option: string) => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Channel
                        </label>
                        <select
                            value={filters.channel}
                            onChange={(event) => handleFilterChange("channel", event.target.value)}
                            aria-label="Filter by channel"
                            className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 text-sm text-slate-700 outline-none transition focus:border-indigo-400 focus:bg-white focus:ring-4 focus:ring-indigo-50"
                        >
                            <option value="">All Channels</option>
                            {filterOptions.channel.map((option: string) => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Target Group
                        </label>
                        <select
                            value={filters.target}
                            onChange={(event) => handleFilterChange("target", event.target.value)}
                            aria-label="Filter by target group"
                            className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 text-sm text-slate-700 outline-none transition focus:border-indigo-400 focus:bg-white focus:ring-4 focus:ring-indigo-50"
                        >
                            <option value="">All Target Groups</option>
                            {filterOptions.target.map((option: string) => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Date Range
                        </label>
                        <select
                            value={dateRange}
                            onChange={(event) => setDateRange(event.target.value)}
                            aria-label="Filter by date range"
                            className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 text-sm text-slate-700 outline-none transition focus:border-indigo-400 focus:bg-white focus:ring-4 focus:ring-indigo-50"
                        >
                            <option value="">All Dates</option>
                            <option value="today">Today</option>
                            <option value="7days">Last 7 Days</option>
                            <option value="30days">Last 30 Days</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Campaign Table */}
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                {filteredCampaigns.length === 0 ? (
                    <div className="flex min-h-[260px] flex-col items-center justify-center px-5 py-10 text-center">
                        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100 text-xl text-slate-400">
                            <FaBullhorn />
                        </div>

                        <h3 className="font-semibold text-slate-800">
                            {campaigns.length === 0 ? "No campaigns found" : "No matching campaigns"}
                        </h3>

                        <p className="mt-1 text-sm text-slate-500">
                            {campaigns.length === 0
                                ? "Your campaigns will appear here once they have been created."
                                : "Try changing your search or filters."}
                        </p>

                        {campaigns.length > 0 && (
                            <button
                                type="button"
                                onClick={resetFilters}
                                className="mt-4 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700"
                            >
                                Clear filters
                            </button>
                        )}
                    </div>
                ) : (
                    <div
                        className="ag-theme-quartz w-full"
                        style={
                            {
                                height: "min(70vh, 720px)",
                                minHeight: "420px",
                                "--ag-font-family": "inherit",
                                "--ag-font-size": "12px",
                                "--ag-border-color": "#E2E8F0",
                                "--ag-header-background-color": "#F8FAFC",
                                "--ag-header-foreground-color": "#475569",
                                "--ag-row-hover-color": "#F1F5FF",
                                "--ag-odd-row-background-color": "#FAFBFF",
                                "--ag-row-border-color": "#F1F5F9",
                                "--ag-header-height": "48px",
                                "--ag-row-height": "58px",
                                "--ag-wrapper-border-radius": "0px",
                            } as React.CSSProperties
                        }
                    >
                        <AgGridReact
                            rowData={filteredCampaigns}
                            columnDefs={columnDefs}
                            defaultColDef={defaultColDef}
                            animateRows
                            suppressCellFocus
                            pagination
                            paginationPageSize={20}
                            headerHeight={48}
                            rowHeight={58}
                        />
                    </div>
                )}
            </div>
        </div>
    );
});

export default VoterReachOutListCampaign;
